/**
 * REV-815: semantic colour tokens for charts and reports.
 *
 * WHY THIS IS IN `contract/`
 * -------------------------
 * The other files here are zod response schemas; this one is a frozen
 * constant. It lives here anyway because it has the same job: it is a shape
 * both sides of the wire agree on, vendored into revenant-ui by
 * `scripts/check-contract-sync.js` so the UI and the export services cannot
 * drift. Before this existed they had:
 *
 *   critical = #C62828   HomePage
 *   critical = #b71c1c   IssuesBreakdownReportPage
 *   critical = #dc3545   auditReportService's exported HTML
 *
 * Three different reds for one severity, in one product, and two of them
 * failed contrast on the surface they were painted on (see the test).
 *
 * NOT IN THE BARREL. `index.js` is normalised into `export * from './scanRun'`
 * by a regex that matches its exact current body, so adding a second spread
 * there silently breaks the vendor sync. Import this module directly, the way
 * revenant-ui imports `@contract/criterionNarrative`.
 *
 * DATA ONLY, AND WRITTEN OUT LONGHAND
 * -----------------------------------
 * There are no helper functions here, and the table is spelled out literally
 * rather than built by a `token(light, dark)` constructor, because the vendored
 * copy is type-checked as TypeScript under `strict`. A plain-JS helper's
 * parameters come through as implicit `any` and fail the UI's typecheck, and a
 * dynamic `table[key]` lookup fails `noUncheckedIndexedAccess`. Literals infer
 * cleanly in both languages; a constructor would have to be written twice.
 *
 * For the same reason the accessors live outside the contract, once per
 * language: `src/utils/chartTokens.js` here, `src/utils/chartTokens.ts` in
 * revenant-ui. Duplicating ten lines of lookup is fine. Duplicating the colour
 * values is the thing that went wrong.
 *
 * STRUCTURE
 * ---------
 * Every token is `{ light: { fill, text }, dark: { fill, text } }`.
 *
 *   fill  the colour of the mark — a bar, a slice, a line, a badge background
 *   text  the colour to put ON that fill when the fill sits behind a label
 *
 * Two themes rather than one palette, because the same hex cannot serve both:
 * #7B1FA2 is 8.20:1 on white and 2.03:1 on the dark paper. A single palette is
 * how dark mode shipped broken.
 *
 * KEYS ARE THE STORED VALUES. VERBATIM.
 * ------------------------------------
 * Every key in a semantic group is spelled exactly as the producing model
 * spells it — `partially_supports`, not `partiallySupports`. This is not a
 * style choice; it is the whole reliability story.
 *
 * The first version of this file used camelCase, and `tokenFor` lowercases both
 * sides before comparing, so `partially_supports` never matched
 * `partiallysupports`. Seven of the eight real conformance and verdict values
 * resolved to the neutral grey, and `not_applicable` resolved to grey *by
 * coincidence* — so the one value a reviewer would spot-check looked correct
 * while `does_not_support` and `fail` rendered as neutral. The unit test missed
 * it because the test invented its own input (`'DOESNOTSUPPORT'`), a string no
 * producer in this system emits.
 *
 * `tests/unit/contracts/chartTokens.test.js` now drives the group keys from
 * `CriterionEvaluation.CONFORMANCE_LEVELS`, `TestEvaluation.VERDICTS` and the
 * models' own ENUM values, so the table and the database cannot drift again.
 * Add a group that mirrors a stored vocabulary and you must add it there too.
 *
 * NOT COVERED, DELIBERATELY: `RemediationItem.priority` is a fourth vocabulary
 * (`critical` / `high` / `normal` / `low`) and nothing charts it today. It is
 * named here rather than silently handled so that whoever first charts it adds
 * a group and a parity case instead of discovering that `high`, `normal` and
 * `low` all render grey. Note it shares only `critical` with `severity`, so
 * reusing the severity group would be wrong in three of four cases.
 *
 * MEASURED (worst case per group per theme; the test recomputes all of it):
 *
 *   group        theme   fill/canvas   text/fill   min ΔE between members
 *   severity     light      4.60          4.80          24.6
 *   severity     dark       7.75          9.76          48.8
 *   conformance  light      4.61          4.81          48.5
 *   verdict      light      4.60          4.80          37.9
 *   wcagLevel    light      6.34          6.61          30.2
 *   series       light      4.91          5.13          35.8
 *
 * Every light value is also the value the export services use, because an
 * exported report is always on paper white.
 */

/**
 * The surfaces a chart is drawn on, per theme. Both are listed because a chart
 * can sit on either a Paper panel or the page background, and a token has to
 * clear 3:1 on whichever one it lands on.
 *
 * These mirror revenant-ui's MUI theme (`palette.background.paper` and
 * `.default`). revenant-ui has a test asserting they still match — the API
 * cannot import the UI's theme, so that seam is checked from the UI side
 * rather than assumed here.
 */
const CHART_CANVASES = Object.freeze({
  light: Object.freeze(['#FFFFFF', '#FAFAFA']),
  dark: Object.freeze(['#1E1E1E', '#121212']),
});

const chartTokens = Object.freeze({
  // The four values FindingDefinition.severity and
  // FindingInstance.severity_override hold. Ordered, but each step still has
  // to be tellable from its neighbour on a stacked bar: `major` moved from
  // #BF360C to #A84300 because #BF360C sat only 14.0 ΔE from `critical`.
  severity: Object.freeze({
    critical: Object.freeze({
      light: Object.freeze({ fill: '#C62828', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#EF9A9A', text: '#000000' }),
    }),
    major: Object.freeze({
      light: Object.freeze({ fill: '#A84300', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#FFA726', text: '#000000' }),
    }),
    minor: Object.freeze({
      light: Object.freeze({ fill: '#8D6E00', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#DCE775', text: '#000000' }),
    }),
    info: Object.freeze({
      light: Object.freeze({ fill: '#0277BD', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#4FC3F7', text: '#000000' }),
    }),
  }),
  // CriterionEvaluation.conformance_level — the VPAT 2.5 vocabulary, rendered
  // by the audit report and the VPAT export. Keys are the STORED values, not a
  // prettier camelCase spelling of them; see the note above about why.
  conformance: Object.freeze({
    supports: Object.freeze({
      light: Object.freeze({ fill: '#2E7D32', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#81C784', text: '#000000' }),
    }),
    partially_supports: Object.freeze({
      light: Object.freeze({ fill: '#8D6E00', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#FFD54F', text: '#000000' }),
    }),
    does_not_support: Object.freeze({
      light: Object.freeze({ fill: '#C62828', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#EF9A9A', text: '#000000' }),
    }),
    not_applicable: Object.freeze({
      light: Object.freeze({ fill: '#455A64', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#B0BEC5', text: '#000000' }),
    }),
  }),
  // TestEvaluation.verdict — a test outcome, which is NOT a conformance claim.
  // Separate group rather than shared keys: the two vocabularies overlap only
  // at not_applicable, and collapsing them would let a `fail` be read as a
  // conformance verdict, which invariant 3 forbids a machine from asserting.
  // not_tested is deliberately far from not_applicable — "nobody looked" and
  // "does not apply" are different answers and must not look alike.
  verdict: Object.freeze({
    pass: Object.freeze({
      light: Object.freeze({ fill: '#2E7D32', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#81C784', text: '#000000' }),
    }),
    fail: Object.freeze({
      light: Object.freeze({ fill: '#C62828', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#EF9A9A', text: '#000000' }),
    }),
    not_applicable: Object.freeze({
      light: Object.freeze({ fill: '#455A64', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#B0BEC5', text: '#000000' }),
    }),
    not_tested: Object.freeze({
      light: Object.freeze({ fill: '#0277BD', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#4FC3F7', text: '#000000' }),
    }),
    // REV-1072: the engine looked and no person has confirmed it. Amber
    // because it is the one verdict that is a REQUEST — a reviewer has
    // something waiting — rather than a finding. Deliberately far from
    // not_tested's blue: "nobody has looked" and "looked, needs confirming"
    // are different states and a reader must not have to guess which.
    needs_review: Object.freeze({
      light: Object.freeze({ fill: '#A64600', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#FFB74D', text: '#000000' }),
    }),
  }),
  // CriterionEvaluation.wcag_level.
  wcagLevel: Object.freeze({
    A: Object.freeze({
      light: Object.freeze({ fill: '#283593', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#9FA8DA', text: '#000000' }),
    }),
    AA: Object.freeze({
      light: Object.freeze({ fill: '#00695C', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#4DB6AC', text: '#000000' }),
    }),
    AAA: Object.freeze({
      light: Object.freeze({ fill: '#7B1FA2', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#CE93D8', text: '#000000' }),
    }),
  }),
  // The categorical ramp: anything with no semantic colour of its own —
  // content category, rule, page. The only requirements are that the slices
  // are distinguishable from each other and visible on the canvas.
  series: Object.freeze({
    s1: Object.freeze({
      light: Object.freeze({ fill: '#1565C0', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#64B5F6', text: '#000000' }),
    }),
    s2: Object.freeze({
      light: Object.freeze({ fill: '#7B1FA2', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#CE93D8', text: '#000000' }),
    }),
    s3: Object.freeze({
      light: Object.freeze({ fill: '#2E7D32', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#81C784', text: '#000000' }),
    }),
    s4: Object.freeze({
      light: Object.freeze({ fill: '#BF360C', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#FFB74D', text: '#000000' }),
    }),
    s5: Object.freeze({
      light: Object.freeze({ fill: '#00695C', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#4DB6AC', text: '#000000' }),
    }),
    s6: Object.freeze({
      light: Object.freeze({ fill: '#AD1457', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#F48FB1', text: '#000000' }),
    }),
  }),
  // Where an unrecognised value lands. Its own group on purpose: the fallback
  // used to point at verdict.notApplicable, which meant renaming a member of a
  // semantic group silently broke the fallback for every group.
  neutral: Object.freeze({
    unknown: Object.freeze({
      light: Object.freeze({ fill: '#455A64', text: '#FFFFFF' }),
      dark: Object.freeze({ fill: '#B0BEC5', text: '#000000' }),
    }),
  }),
});

/**
 * Slot order for the categorical ramp.
 *
 * Six, not more. Adding slots 7 and 8 (brown and indigo) was tried and
 * rejected: indigo sits 19.4 ΔE from slot 1's blue in the dark theme, and
 * swapping it for a slate put brown and slate 14.8 apart instead. Past about
 * six categories, colours that all clear 3:1 on the canvas stop being
 * reliably distinguishable from one another, so the honest answer is to chart
 * the top five or six and group the tail as one "Other" slice rather than to
 * invent a seventh colour nobody can name.
 */
const SERIES_ORDER = Object.freeze(['s1', 's2', 's3', 's4', 's5', 's6']);

// Deliberately one line. Prettier never collapses an object literal that has a
// newline after its `{`, so a multi-line export here normalises into a
// multi-line `export { ... }` in the vendored copy — which revenant-ui's
// lint-staged then collapses on commit, producing drift the sync check fails on
// and no one caused on purpose. One line is stable under both formatters.
export { chartTokens, CHART_CANVASES, SERIES_ORDER };
